import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ConnectableObservable, Observable, empty } from "rxjs";
import { publishLast } from "rxjs/operators";

import { withSpinner } from "../../extensions/withSpinner.extension";
import { ChangeTracked, ScheduleVendorAcctCategoryAssoc, ScheduleTaskTradePartnerView, ScheduleTradePartnerView, Vendor } from "../../types";
import { API_ROOT } from "../../types/constants/appconstants";

@Injectable({
    "providedIn": "root"
})
export class VendorService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private vendorsBaseUrl = this.apiRoot + "api/vendors";

    private lastMarketId: number;
    private lastMarketId_Inactive: number;
    private lastIncludeAddress: boolean;
    private financialVendorsObs: ConnectableObservable<Vendor[]>;
    private vendorsObs: ConnectableObservable<Vendor[]>;
    private vendorsObs_Inactive: ConnectableObservable<Vendor[]>;

    getVendorsByMarket(marketId: number, includeInactive: boolean = false): Observable<Vendor[]> {
        if (includeInactive) {
            if (this.lastMarketId_Inactive !== marketId) {
                this.vendorsObs_Inactive = withSpinner(this.http).get<Vendor[]>(`${this.vendorsBaseUrl}/${marketId}/vendors/${includeInactive}`).pipe(
                    publishLast()
                ) as ConnectableObservable<Vendor[]>;
                this.vendorsObs_Inactive.connect();

                this.lastMarketId_Inactive = marketId;
            }

            return this.vendorsObs_Inactive;
        } else {
            if (this.lastMarketId !== marketId) {
                this.vendorsObs = withSpinner(this.http).get<Vendor[]>(`${this.vendorsBaseUrl}/${marketId}/vendors`).pipe(
                    publishLast()
                ) as ConnectableObservable<Vendor[]>;
                this.vendorsObs.connect();

                this.lastMarketId = marketId;
            }

            return this.vendorsObs;
        }
    }

    getTradePartnersForJob(jobId: number, marketId: number): Observable<ScheduleVendorAcctCategoryAssoc[]> {
        const thereIsNoJobId: boolean = jobId === null || typeof jobId === "undefined";
        if(thereIsNoJobId) {
            const anEmptyArrayObservable = new Observable<ScheduleVendorAcctCategoryAssoc[]>((sub) => { sub.next([]); });
            return anEmptyArrayObservable;
        }

        return withSpinner(this.http).get<ScheduleVendorAcctCategoryAssoc[]>(`${this.vendorsBaseUrl}/${marketId}/job/${jobId}/tradepartners`);
    }

    saveScheduleVendorAcctCategoryAssocs(scheduleTradePartners: ScheduleTradePartnerView[] & ChangeTracked<ScheduleTradePartnerView[]>): Observable<boolean> {
        return withSpinner(this.http).post<boolean>(`${this.vendorsBaseUrl}/schedules/tradepartners`, { "scheduleTradePartners": scheduleTradePartners });
    }

    saveScheduleTaskVendorAcctCategoryAssocs(scheduleVendorAcctCategoryAssoc: ScheduleTaskTradePartnerView & ChangeTracked<ScheduleTaskTradePartnerView>): Observable<boolean> {
        return withSpinner(this.http).post<boolean>(`${this.vendorsBaseUrl}/scheduletasks/tradepartners`, { scheduleVendorAcctCategoryAssoc: scheduleVendorAcctCategoryAssoc });
    }

    getFinancialVendors(marketId: number, includeAddress: boolean = false): Observable<Vendor[]> {
        if(!this.financialVendorsObs || includeAddress != this.lastIncludeAddress) {
            this.financialVendorsObs = withSpinner(this.http).get<Vendor[]>(`${this.vendorsBaseUrl}/getfinancialvendors/${marketId}/${includeAddress}`).pipe(
                publishLast()
            ) as ConnectableObservable<Vendor[]>;
            this.financialVendorsObs.connect();

            this.lastIncludeAddress = includeAddress
        }

        return this.financialVendorsObs
    }
}
